<template>
  <div class="image-label">
    <div class="image-content">
      <el-radio-group
        v-model="drawMode"
        size="large"
        fill="#409eff"
        @change="onSetMode"
      >
        <el-radio-button value="PAN">
          <el-tooltip content="鼠标" placement="top">
            <el-icon><Pointer /></el-icon>
          </el-tooltip>
        </el-radio-button>
        <el-radio-button value="RECT" v-if="labelValue">
          <el-tooltip content="框选目标" placement="top">
            <el-icon><Edit /></el-icon>
          </el-tooltip>
        </el-radio-button>
      </el-radio-group>

      <div
        v-loading.fullscreen.lock="loading"
        element-loading-text="加载中......"
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.1)"
        id="AILabel-container"
      >
        <div
          ref="popoverRef"
          class="popover-anchor"
          :style="{ left: menuX + 'px', top: menuY + 'px' }"
        />
      </div>

      <el-popover
        v-model:visible="popoverVisible"
        :virtual-ref="popoverRef"
        virtual-triggering
        trigger="manual"
        placement="right-start"
        width="140"
        :show-arrow="false"
      >
        <div class="menu-item danger" @click="deleteLabel">删除</div>
      </el-popover>
    </div>

    <LabelSelect ref="LabelSelectRef" @change="labelChange" />
  </div>
</template>

<script setup>
import AILabel from "ailabel";
import {
  ref,
  defineEmits,
  defineExpose,
  nextTick,
  getCurrentInstance,
} from "vue";
import { Pointer, Edit } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import LabelSelect from "./LabelSelect.vue";

/* ================= 基础 ================= */
const emit = defineEmits(["change"]);
const { proxy } = getCurrentInstance();

const loading = ref(false);
const fileUrl = ref("");
const imageBlob = ref("");

const imageW = ref(0);
const imageH = ref(0);

const ailabelRef = ref(null);
const imageLayer = ref(null);
const featureLayer = ref(null);
const textLayer = ref(null);

/* ================= Label ================= */
const labelValue = ref(null);
const labelChange = (val) => {
  labelValue.value = val;
};

/* ================= 右键菜单 ================= */
const popoverRef = ref();
const popoverVisible = ref(false);
const clickRegion = ref(null);
const menuX = ref(0);
const menuY = ref(0);

/* ================= 绘制模式 ================= */
const drawMode = ref("PAN");
const onSetMode = (mode) => {
  drawMode.value = mode;
  ailabelRef.value?.setMode(mode);
};

/* ================= 标注数据 ================= */
const labelList = ref([]);

/* ================== 核心补丁状态 ================== */
// drawDone 后短时间内忽略 click
let lastDrawTime = 0;
const IGNORE_CLICK_AFTER_DRAW = 300;

// 全局唯一 id 计数器（回显 / 新画都不会重复）
let uidSeed = Date.now();
const genUID = () => `rect-${uidSeed++}`;

/* ================= open：唯一入口 ================= */
const open = async (question, answer) => {
  try {
    loading.value = true;
    fileUrl.value = question.url;

    const labelOptions = JSON.parse(question.problem);
    proxy.$refs.LabelSelectRef.setOptions(labelOptions);

    await destroyAILabel();
    await loadImage();
    await nextTick();
    await ailabelInit();

    if (Array.isArray(answer) && answer.length) {
      for (const item of answer) {
        const { cls, xmin, ymin, xmax, ymax } = item;
        const labelData = labelOptions.find((i) => i.id === cls);
        if (!labelData) continue;

        await drawRect(
          {
            x: xmin,
            y: ymin,
            width: xmax - xmin,
            height: ymax - ymin,
          },
          labelData,
          true,
        );
      }
    }
  } finally {
    loading.value = false;
    labelValue.value = null;
  }
};

/* ================= 销毁 ================= */
const destroyAILabel = async () => {
  if (ailabelRef.value) {
    ailabelRef.value.destroy();
    ailabelRef.value = null;
  }
  labelList.value = [];
  popoverVisible.value = false;
};

/* ================= 加载图片 ================= */
const loadImage = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const res = await fetch(fileUrl.value);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      imageBlob.value = url;

      const img = new Image();
      img.onload = () => {
        imageW.value = img.width;
        imageH.value = img.height;
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });
};

/* ================= AILabel 初始化 ================= */
const ailabelInit = async () => {
  const container = document.getElementById("AILabel-container");
  if (!container) return;

  const cw = container.clientWidth || 900;
  const ch = container.clientHeight || 600;
  const scale = Math.min(cw / imageW.value, ch / imageH.value);
  const zoom = (cw / scale) * 0.9;

  const map = new AILabel.Map("AILabel-container", {
    center: { x: imageW.value / 2, y: imageH.value / 2 },
    zoom,
    zoomWhenDrawing: true,
    panWhenDrawing: true,
    withHotKeys: true,
  });

  ailabelRef.value = map;

  map.setDrawingStyle({
    strokeStyle: "#409EFF",
    fillStyle: "#409EFF",
    lineWidth: 4,
  });

  initImageLayer();
  initFeatureLayer();
  initTextLayer();
  initEvents();
};

/* ================= 图层 ================= */
const initImageLayer = () => {
  imageLayer.value = new AILabel.Layer.Image(
    "image-layer",
    {
      src: imageBlob.value,
      width: imageW.value,
      height: imageH.value,
      position: { x: 0, y: 0 },
    },
    {},
    { zIndex: 5 },
  );
  ailabelRef.value.addLayer(imageLayer.value);
};

const initFeatureLayer = () => {
  featureLayer.value = new AILabel.Layer.Feature(
    "feature-layer",
    {},
    { zIndex: 10, opacity: 0.5 },
  );
  ailabelRef.value.addLayer(featureLayer.value);
};

const initTextLayer = () => {
  textLayer.value = new AILabel.Layer.Text("text-layer", {}, { zIndex: 12 });
  ailabelRef.value.addLayer(textLayer.value);
};

/* ================= 事件 ================= */
const initEvents = () => {
  // 绘制完成
  ailabelRef.value.events.on("drawDone", (type, data) => {
    lastDrawTime = Date.now(); // ✅ 核心补丁

    if (!labelValue.value) {
      ElMessage.warning("请先选择标签");
      return;
    }

    if (
      data.x < 0 ||
      data.y < 0 ||
      data.x + data.width > imageW.value ||
      data.y + data.height > imageH.value
    ) {
      ElMessage.warning("请在图片内部标注！");
      return;
    }

    drawRect(data, labelValue.value);
  });

  // 点击
  ailabelRef.value.events.on("click", (e) => {
    // ✅ 核心补丁 1：忽略 drawDone 后的 click
    if (Date.now() - lastDrawTime < IGNORE_CLICK_AFTER_DRAW) return;

    const hit = hitTest(e.global);
    if (!hit) return;

    // ✅ 核心补丁 2：保证切换目标时 popover 重新打开
    // popoverVisible.value = false;

    clickRegion.value = hit;
    menuX.value = e.screen.x;
    menuY.value = e.screen.y;
    setTimeout(() => {
      popoverVisible.value = true;
    }, 200);
  });
};

/* ================= 删除 ================= */
const deleteLabel = () => {
  const id = clickRegion.value?.id;
  if (!id) return;

  featureLayer.value.removeFeatureById(id);
  textLayer.value.removeTextById(id);

  labelList.value = labelList.value.filter((i) => i.id !== id);

  emitAnswer();
  popoverVisible.value = false;
  ElMessage.success("删除成功");
};

/* ================= 绘制 ================= */
const drawRect = async (shape, form, isRestore = false) => {
  const id = genUID(); // ✅ 唯一 id

  const rect = await new AILabel.Feature.Rect(id, shape, form, {
    strokeStyle: form.color,
    fillStyle: form.color,
    globalAlpha: 0.2,
    stroke: true,
    fill: true,
  });

  featureLayer.value.addFeature(rect);

  const text = await new AILabel.Text(
    id,
    {
      text: form.label,
      position: { x: shape.x, y: shape.y },
      offset: { x: 0, y: -26 },
    },
    form,
    {
      fillStyle: form.color,
      background: true,
      fontColor: "#ffffff",
      lineWidth: 0,
      strokeStyle: "transparent",
      font: "normal 20px Arial",
    },
  );

  textLayer.value.addText(text);

  labelList.value.push({ id, shape, props: form });

  if (!isRestore && !loading.value) {
    emitAnswer();
    onSetMode("PAN");
  }
};

const emitAnswer = () => {
  emit(
    "change",
    labelList.value.map((i) => ({
      cls: i.props.id,
      xmin: i.shape.x,
      ymin: i.shape.y,
      xmax: i.shape.x + i.shape.width,
      ymax: i.shape.y + i.shape.height,
    })),
  );
};

/* ================= 命中检测 ================= */
const hitTest = (point) => {
  for (const item of [...labelList.value].reverse()) {
    const s = item.shape;
    if (
      point.x >= s.x &&
      point.x <= s.x + s.width &&
      point.y >= s.y &&
      point.y <= s.y + s.height
    ) {
      return item;
    }
  }
  return null;
};

defineExpose({ open });
</script>

<style scoped lang="less">
/* ✅ 样式：完全未改 */
.image-label {
  display: flex;
  height: 600px;
  justify-content: space-between;
  align-items: flex-start;
}
.image-content {
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
#AILabel-container {
  margin-top: 12px;
  background-color: rgb(177, 179, 184);
  width: 100%;
  height: calc(100% - 100px);
  position: relative;
  overflow: hidden;
}
.popover-anchor {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}
.menu-item {
  cursor: pointer;
}
.menu-item.danger {
  color: #ef4444;
}
.menu-item:hover {
  background: #f3f4f6;
}
</style>
